import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { MessageCircle, X, Send, Users, Smile, Trash2, AlertTriangle, Sticker, PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { toast } from "sonner";

const VIP_COLORS = {
  Royal: "text-[#4d7cff]",
  Ruby: "text-red-400",
  Diamond: "text-cyan-400",
  Platinum: "text-purple-300",
  Gold: "text-[#ffd86b]",
  Silver: "text-zinc-300",
  Bronze: "text-[#c47a3a]"
};

const VIP_BADGES = {
  Royal: "\u{1F451}",
  Ruby: "\u{1F534}",
  Diamond: "\u{1F48E}",
  Platinum: "\u2B50",
  Gold: "\u{1F3C6}",
  Silver: "\u{1F948}",
  Bronze: "\u{1F949}"
};

// Expanded emoji set organized by category
const EMOJI_CATEGORIES = {
  Arcade: [
    "\u{1F525}","\u{1F3C6}","\u{1F3AE}","\u{1F579}\uFE0F","\u{1F3AF}","\u{1F680}",
    "\u{1F451}","\u2B50","\u{1F48E}","\u{1FA99}","\u{1F4AB}","\u{1F31F}",
    "\u26A1","\u{1F3C5}","\u{1F396}\uFE0F","\u{1F38A}"
  ],
  Faces: [
    "\u{1F60E}","\u{1F602}","\u{1F622}","\u{1F620}","\u{1F60D}","\u{1F914}",
    "\u{1F92F}","\u{1F929}","\u{1F973}","\u{1F612}","\u{1F62E}","\u{1F62D}",
    "\u{1F976}","\u{1F975}","\u{1F47B}","\u{1F608}"
  ],
  Hands: [
    "\u{1F44D}","\u{1F44F}","\u{1F44B}","\u{1F91E}","\u{1F4AA}","\u{1F64F}",
    "\u{1F44E}","\u270C\uFE0F","\u{1F91F}","\u{1F919}","\u{1F448}","\u{1F449}"
  ],
  Objects: [
    "\u{1F680}","\u{1F389}","\u{1F381}","\u{1F4AF}","\u2764\uFE0F","\u{1F4A5}",
    "\u{1F4B8}","\u{1F4A8}","\u{1F3AF}","\u{1F52E}","\u{1F4AB}","\u{1F397}\uFE0F"
  ],
};

// Stickers - larger arcade themed
const STICKERS = [
  { id: "trophy", emoji: "\u{1F3C6}", label: "Winner!" },
  { id: "crown", emoji: "\u{1F451}", label: "Royal!" },
  { id: "fire", emoji: "\u{1F525}", label: "On Fire!" },
  { id: "rocket", emoji: "\u{1F680}", label: "Moon!" },
  { id: "diamond", emoji: "\u{1F48E}", label: "Diamond!" },
  { id: "party", emoji: "\u{1F389}", label: "Party!" },
  { id: "arcade", emoji: "\u{1F3AE}", label: "Arcade!" },
  { id: "joystick", emoji: "\u{1F579}\uFE0F", label: "Player 1!" },
  { id: "target", emoji: "\u{1F3AF}", label: "Bullseye!" },
  { id: "lightning", emoji: "\u26A1", label: "Zap!" },
  { id: "star", emoji: "\u{1F31F}", label: "Star!" },
  { id: "100", emoji: "\u{1F4AF}", label: "100!" },
];

// Quick reaction emojis
const QUICK_REACTIONS = ["\u{1F525}","\u{1F44D}","\u2764\uFE0F","\u{1F602}","\u{1F62E}","\u{1F3AE}","\u{1F3C6}","\u{1F451}","\u{1F48E}","\u{1F3AF}","\u26A1","\u{1F31F}","\u{1F4AF}","\u{1F60E}","\u{1F680}","\u{1F44F}"];

const getAvatarColor = (username) => {
  const colors = [
    "bg-red-500","bg-orange-500","bg-amber-500","bg-yellow-500",
    "bg-lime-500","bg-green-500","bg-emerald-500","bg-teal-500",
    "bg-cyan-500","bg-sky-500","bg-blue-500","bg-indigo-500",
    "bg-violet-500","bg-purple-500","bg-fuchsia-500","bg-pink-500"
  ];
  let hash = 0;
  for (let i = 0; i < username.length; i++) hash = username.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const AVATAR_IMAGES = {
  cool_guy: "/avatars/cool_guy.png",
  lucky_lady: "/avatars/lucky_lady.png",
  mystery_man: "/avatars/mystery_man.png",
  golden_king: "/avatars/golden_king.png",
  lion: "/avatars/lion.png",
  wolf: "/avatars/wolf.png",
  robot: "/avatars/robot.png",
  phoenix: "/avatars/phoenix.png",
  dragon: "/avatars/dragon.png",
  queen: "/avatars/queen.png",
  ninja: "/avatars/ninja.png",
  diamond_skull: "/avatars/diamond_skull.png",
  dice_queen: "/avatars/dice_queen.png",
  card_queen: "/avatars/card_queen.png",
  casino_boss: "/avatars/casino_boss.png",
  holiday_girls: "/avatars/holiday_girls.png",
  lucky_leprechaun: "/avatars/lucky_leprechaun.png",
  chip_queen: "/avatars/chip_queen.png",
  magic_genie: "/avatars/magic_genie.png",
  ace_lady: "/avatars/ace_lady.png",
  red_king: "/avatars/red_king.png",
  purple_king: "/avatars/purple_king.png",
  dark_dealer: "/avatars/dark_dealer.png",
};

const isEmojiAvatar = (val) => {
  if (!val || typeof val !== "string") return false;
  if (val.startsWith("http")) return false;
  if (AVATAR_IMAGES[val]) return false;
  return val.trim().length > 0 && val.trim().length <= 8;
};

const getAvatarUrl = (avatarId) => {
  if (!avatarId) return null;
  if (avatarId.startsWith("http")) return avatarId;
  return AVATAR_IMAGES[avatarId] || null;
};

const LiveChatPanel = ({ onClose }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [onlineCount, setOnlineCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState("emoji"); // "emoji" | "stickers"
  const [emojiCategory, setEmojiCategory] = useState("Arcade");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [reactingTo, setReactingTo] = useState(null); // msg id for reaction picker
  // Scroll-lock (Feb 2026): if the user has scrolled up more than 50px
  // from the bottom, do NOT auto-scroll when new messages arrive.
  // Instead show a "New Messages" pill that jumps to the latest on tap.
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesContainerRef = useRef(null);
  // Dock mode: "right" (default) | "left"
  const [dockMode, setDockMode] = useState(() => {
    try { return localStorage.getItem("jr_chatpanel_dock") || "right"; } catch { return "right"; }
  });
  const setDock = (mode) => {
    setDockMode(mode);
    try { localStorage.setItem("jr_chatpanel_dock", mode); } catch {}
  };
  const messagesEndRef = useRef(null);
  const pickerRef = useRef(null);
  const reactionRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isAdmin = user?.role === "admin" || user?.is_admin;

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?limit=100`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch messages:", e);
    }
  };

  const fetchOnlineCount = async () => {
    try {
      const res = await axios.get(`${API}/chat/online-count`);
      setOnlineCount(res.data.count);
    } catch (e) {}
  };

  useEffect(() => {
    fetchMessages();
    fetchOnlineCount();
    const messageInterval = setInterval(fetchMessages, 3000);
    const countInterval = setInterval(fetchOnlineCount, 30000);
    return () => { clearInterval(messageInterval); clearInterval(countInterval); };
  }, []);

  useEffect(() => {
    // Scroll-lock aware: only auto-scroll when the user is already at (or
    // near) the bottom. If they've scrolled up >50px we treat it as a
    // deliberate lock, do NOT snap them back, and bump the unread pill.
    const el = messagesContainerRef.current;
    if (!el) return;
    if (isScrollLocked) {
      setUnreadCount((n) => n + 1);
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const jumpToLatest = () => {
    setUnreadCount(0);
    setIsScrollLocked(false);
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const locked = distanceFromBottom > 50;
    setIsScrollLocked(locked);
    if (!locked) setUnreadCount(0);
  };

  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
      if (reactionRef.current && !reactionRef.current.contains(e.target)) setReactingTo(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const insertEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowPicker(false);
  };

  const sendSticker = async (sticker) => {
    if (!token || sending) return;
    setSending(true);
    setShowPicker(false);
    try {
      await axios.post(`${API}/chat/send`, { message: `[sticker:${sticker.id}] ${sticker.emoji}` }, { headers });
      await fetchMessages();
    } catch (e) {
      console.error("Failed to send sticker:", e);
    }
    setSending(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/send`, { message: newMessage }, { headers });
      setNewMessage("");
      await fetchMessages();
    } catch (e) {
      console.error("Failed to send message:", e);
    }
    setSending(false);
  };

  const deleteMessage = async (msgId) => {
    try {
      await axios.delete(`${API}/chat/message/${msgId}`, { headers });
      setMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success("Message deleted");
    } catch (e) {
      toast.error("Failed to delete message");
    }
  };

  const resetChat = async () => {
    try {
      const res = await axios.delete(`${API}/chat/clear-all`, { headers });
      setMessages([]);
      setShowResetConfirm(false);
      toast.success(`Chat cleared (${res.data.deleted} messages removed)`);
    } catch (e) {
      toast.error("Failed to clear chat");
    }
  };

  const toggleReaction = async (msgId, emoji) => {
    if (!token) return;
    try {
      const res = await axios.post(`${API}/chat/react/${msgId}`, { emoji }, { headers });
      // Update message reactions locally
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, reactions: res.data.reactions } : m));
      setReactingTo(null);
    } catch (e) {
      console.error("Failed to react:", e);
    }
  };

  const parseUTC = (timestamp) => {
    if (!timestamp) return new Date();
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !/\d{2}:\d{2}$/.test(timestamp.slice(-6))) {
      return new Date(timestamp + 'Z');
    }
    return new Date(timestamp);
  };

  const formatTime = (timestamp) => parseUTC(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const formatDate = (timestamp) => {
    const date = parseUTC(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const groupMessagesByDate = (msgs) => {
    const groups = {};
    if (!Array.isArray(msgs)) return groups;
    msgs.forEach(msg => {
      const dateKey = formatDate(msg.timestamp);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(msg);
    });
    return groups;
  };

  const isSticker = (text) => text.startsWith("[sticker:");
  const getStickerEmoji = (text) => {
    const match = STICKERS.find(s => text.includes(`[sticker:${s.id}]`));
    return match ? match.emoji : text.replace(/\[sticker:\w+\]\s*/, "");
  };

  const groupedMessages = groupMessagesByDate(messages);
  const currentUserId = user?.id;

  return (
    <>
      {/* Mobile backdrop — tap to close, never on desktop */}
      <div
        className="fixed inset-0 z-[69] bg-black/50 backdrop-blur-[2px] sm:hidden"
        onClick={onClose}
        data-testid="chat-mobile-backdrop"
      />
      <div
        className={`fixed top-0 bottom-[calc(64px+env(safe-area-inset-bottom,0px))] sm:bottom-0 z-[70] ${dockMode === "left" ? "left-0" : "right-0"} pointer-events-none`}
        data-testid="chat-overlay"
      >
        <div
          className={`pointer-events-auto bg-[#0f1419] border border-zinc-800 shadow-2xl flex flex-col overflow-hidden h-full w-[88vw] max-w-[380px] sm:w-[380px] ${dockMode === "left" ? "border-l-0 rounded-r-2xl" : "border-r-0 rounded-l-2xl"}`}
          data-testid="chat-panel"
        >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a2332] to-[#0f1419] px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            <span className="font-bold text-white truncate">Live Chat</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-zinc-400">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <Users className="w-3 h-3" />
              <span>{onlineCount}</span>
            </div>
            {/* Dock toggles */}
            <button
              onClick={() => setDock("left")}
              title="Dock to left"
              className={`p-1.5 rounded-md transition ${dockMode === "left" ? "bg-cyan-500/20 text-cyan-300" : "hover:bg-white/10 text-zinc-400 hover:text-white"}`}
              data-testid="chatpanel-dock-left"
            >
              <PanelLeftOpen className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDock("right")}
              title="Dock to right"
              className={`p-1.5 rounded-md transition ${dockMode === "right" ? "bg-cyan-500/20 text-cyan-300" : "hover:bg-white/10 text-zinc-400 hover:text-white"}`}
              data-testid="chatpanel-dock-right"
            >
              <PanelRightOpen className="w-3.5 h-3.5" />
            </button>
            {isAdmin && (
              <button onClick={() => setShowResetConfirm(true)} className="text-xs text-red-400/60 hover:text-red-400 px-2 py-1 rounded hover:bg-red-500/10" data-testid="chat-reset-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="chatpanel-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto p-3 space-y-1 relative"
          ref={messagesContainerRef}
          onScroll={handleScroll}
          data-testid="chat-messages-scroll"
        >
          {Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              <div className="flex items-center justify-center my-3">
                <span className="text-[10px] text-zinc-500 bg-zinc-800/50 px-3 py-1 rounded-full">{date}</span>
              </div>
              {msgs.map((msg) => {
                const avatarUrl = getAvatarUrl(msg.avatar);
                const isEmoji = !avatarUrl && isEmojiAvatar(msg.avatar);
                const msgReactions = msg.reactions || {};
                const isStk = isSticker(msg.message);

                return (
                  <div key={msg.id} className="group hover:bg-zinc-800/30 rounded-lg px-2 py-1.5 transition-colors relative" data-testid={`chat-msg-${msg.id}`}>
                    <div className="flex items-start gap-2">
                      {/* Avatar */}
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={msg.username} className="w-8 h-8 rounded-full object-cover flex-shrink-0 shadow-md" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      {isEmoji ? (
                        <div className={`w-8 h-8 rounded-full ${getAvatarColor(msg.username)} items-center justify-center flex-shrink-0 text-base shadow-md flex`}>
                          <span className="leading-none">{msg.avatar}</span>
                        </div>
                      ) : null}
                      <div className={`w-8 h-8 rounded-full ${getAvatarColor(msg.username)} items-center justify-center flex-shrink-0 text-white font-bold text-xs shadow-md`} style={{ display: (avatarUrl || isEmoji) ? 'none' : 'flex' }}>
                        {msg.username.charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs">{VIP_BADGES[msg.vip_tier] || "\u{1F949}"}</span>
                          <span className={`font-semibold text-sm ${VIP_COLORS[msg.vip_tier] || "text-orange-400"}`}>{msg.username}</span>
                          {msg.is_owner && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-black uppercase tracking-wider border"
                              style={{
                                background: 'linear-gradient(135deg, #ffd700, #b8860b)',
                                borderColor: '#ffe066',
                                color: '#1a0f00',
                                boxShadow: '0 0 8px rgba(255,215,0,0.6)',
                              }}
                              title="Verified Owner"
                              data-testid={`owner-badge-${msg.id}`}
                            >
                              <span aria-hidden="true">👑</span>
                              <span>Owner</span>
                            </span>
                          )}
                          {!msg.is_owner && msg.is_moderator && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-black uppercase tracking-wider border"
                              style={{
                                background: 'linear-gradient(135deg, #c0c8d4, #8a95a8)',
                                borderColor: '#e2e8f0',
                                color: '#0f1419',
                                boxShadow: '0 0 6px rgba(192,200,212,0.5)',
                              }}
                              title="JackRoyals Moderator"
                              data-testid={`mod-badge-${msg.id}`}
                            >
                              <span aria-hidden="true">🛡</span>
                              <span>Mod</span>
                            </span>
                          )}
                          <span className="text-[10px] text-zinc-600 ml-auto">{formatTime(msg.timestamp)}</span>
                          {isAdmin && (
                            <button onClick={() => deleteMessage(msg.id)} className="opacity-0 group-hover:opacity-100 text-red-400/50 hover:text-red-400 ml-1" data-testid={`delete-msg-${msg.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>

                        {/* Message or Sticker */}
                        {isStk ? (
                          <div className="text-5xl mt-1 leading-none">{getStickerEmoji(msg.message)}</div>
                        ) : (
                          <p className="text-sm text-zinc-300 break-words mt-0.5">{msg.message}</p>
                        )}

                        {/* Reactions Display */}
                        {Object.keys(msgReactions).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(msgReactions).map(([emoji, users]) => {
                              const count = Array.isArray(users) ? users.length : 0;
                              if (count === 0) return null;
                              const iReacted = Array.isArray(users) && users.some(u => u.user_id === currentUserId);
                              return (
                                <button
                                  key={emoji}
                                  onClick={() => toggleReaction(msg.id, emoji)}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors ${
                                    iReacted ? "bg-cyan-500/20 border border-cyan-500/40" : "bg-zinc-800 border border-zinc-700 hover:border-zinc-500"
                                  }`}
                                  data-testid={`reaction-${msg.id}-${emoji}`}
                                >
                                  <span>{emoji}</span>
                                  <span className={`font-medium ${iReacted ? "text-cyan-400" : "text-zinc-400"}`}>{count}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Reaction trigger (hover) */}
                      {user && (
                        <button
                          onClick={() => setReactingTo(reactingTo === msg.id ? null : msg.id)}
                          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-cyan-400 p-1 rounded transition-all self-start mt-0.5"
                          data-testid={`react-btn-${msg.id}`}
                        >
                          <Smile className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Reaction Picker Popup */}
                    {reactingTo === msg.id && (
                      <div ref={reactionRef} className="absolute right-0 top-0 -translate-y-full mt-0 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl p-2 z-20 w-[200px]" data-testid={`reaction-picker-${msg.id}`}>
                        <div className="grid grid-cols-8 gap-0.5">
                          {QUICK_REACTIONS.map((emoji) => (
                            <button key={emoji} onClick={() => toggleReaction(msg.id, emoji)} className="w-6 h-6 flex items-center justify-center text-sm hover:bg-zinc-800 rounded transition-colors">
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        {isScrollLocked && (
          <button
            onClick={jumpToLatest}
            className="absolute left-1/2 -translate-x-1/2 bottom-[80px] z-30 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold text-xs shadow-lg animate-in fade-in slide-in-from-bottom duration-200"
            style={{
              background: 'linear-gradient(135deg, #4d7cff, #7f5dff)',
              color: '#fff',
              boxShadow: '0 4px 20px rgba(77,124,255,0.5)',
            }}
            data-testid="new-message-pill"
          >
            <span>↓</span>
            <span>{unreadCount > 0 ? `${unreadCount} New Message${unreadCount === 1 ? '' : 's'}` : 'Jump to Latest'}</span>
          </button>
        )}

        {/* Input Area */}
        {user ? (
          <div className="border-t border-zinc-800 bg-[#0a0d12] flex-shrink-0">
            {/* Emoji/Sticker Picker */}
            {showPicker && (
              <div ref={pickerRef} className="border-b border-zinc-800 bg-zinc-900/95">
                {/* Tabs */}
                <div className="flex border-b border-zinc-800">
                  <button
                    onClick={() => setPickerTab("emoji")}
                    className={`flex-1 py-2 text-xs font-bold transition-colors ${pickerTab === "emoji" ? "text-cyan-400 border-b-2 border-cyan-400" : "text-zinc-500 hover:text-zinc-300"}`}
                    data-testid="picker-emoji-tab"
                  >
                    Emojis
                  </button>
                  <button
                    onClick={() => setPickerTab("stickers")}
                    className={`flex-1 py-2 text-xs font-bold transition-colors ${pickerTab === "stickers" ? "text-cyan-400 border-b-2 border-cyan-400" : "text-zinc-500 hover:text-zinc-300"}`}
                    data-testid="picker-sticker-tab"
                  >
                    Stickers
                  </button>
                </div>

                {pickerTab === "emoji" ? (
                  <div className="p-2">
                    {/* Category tabs */}
                    <div className="flex gap-1 mb-2 overflow-x-auto pb-1">
                      {Object.keys(EMOJI_CATEGORIES).map(cat => (
                        <button
                          key={cat}
                          onClick={() => setEmojiCategory(cat)}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap transition-colors ${
                            emojiCategory === cat ? "bg-cyan-500/20 text-cyan-400" : "text-zinc-500 hover:text-zinc-300"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-8 gap-1 max-h-[120px] overflow-y-auto">
                      {EMOJI_CATEGORIES[emojiCategory].map((emoji, i) => (
                        <button key={i} type="button" onClick={() => insertEmoji(emoji)} className="w-8 h-8 flex items-center justify-center text-lg hover:bg-zinc-800 rounded-lg transition-colors">
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-2 grid grid-cols-4 gap-2 max-h-[160px] overflow-y-auto">
                    {STICKERS.map((sticker) => (
                      <button
                        key={sticker.id}
                        onClick={() => sendSticker(sticker)}
                        className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-zinc-800 transition-colors"
                        data-testid={`sticker-${sticker.id}`}
                      >
                        <span className="text-3xl">{sticker.emoji}</span>
                        <span className="text-[9px] text-zinc-500">{sticker.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSend} className="p-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPicker(!showPicker)}
                  className={`p-2 rounded-full transition-colors ${showPicker ? "text-cyan-400 bg-cyan-500/10" : "text-zinc-400 hover:text-cyan-400 hover:bg-zinc-800/50"}`}
                  data-testid="chat-emote-btn"
                >
                  <Smile className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  maxLength={500}
                  className="flex-1 bg-zinc-800/50 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  data-testid="chat-panel-input"
                />
                <button type="submit" disabled={!newMessage.trim() || sending} className="bg-[#4d7cff] hover:bg-[#3d6cef] disabled:bg-zinc-700 disabled:cursor-not-allowed text-white p-2.5 rounded-full transition-colors" data-testid="chat-panel-send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="p-3 border-t border-zinc-800 bg-[#0a0d12] text-center flex-shrink-0">
            <p className="text-sm text-zinc-500">Log in to chat</p>
          </div>
        )}
      </div>

      {/* Reset Confirm Modal */}
      {showResetConfirm && (
        <div className="absolute inset-0 bg-black/70 z-10 flex items-center justify-center p-6">
          <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-5 max-w-xs w-full">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="font-bold text-white">Clear All Chat</p>
                <p className="text-xs text-zinc-400">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mb-4">Are you sure you want to delete all chat messages?</p>
            <div className="flex gap-2">
              <button onClick={() => setShowResetConfirm(false)} className="flex-1 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-white transition-colors">Cancel</button>
              <button onClick={resetChat} className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-sm text-white font-bold transition-colors" data-testid="chat-reset-confirm">Clear All</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default LiveChatPanel;
